import { IonContent, isPlatform } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { FormInput } from '@/components/FormInput';
import { InfoProtection } from '@/components/InfoProtection';
import { useAccount } from '@/hooks/useAccount';
import { useCreateStripeClient } from '@/hooks/useCreateStripeClient';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingSubtitle } from '@/pages/Onboarding/components/Subtitle';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { checkIsValidateEmail } from '@/pages/Onboarding/utils/checkIsValidEmail';
import { userService } from '@/services/user';
import { trackOnboardingComplete } from '@/utils/analytics';
import { canLogEvents } from '@/utils/canLogEvents';

import { layoutQueryParams } from '../../params';
import { LayoutDataType } from '../../types';

import { Image, PageStyles } from './Email.styles';

export const ScreenEmail: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Email',
      });
    }

    layoutQueryParams.removeStepsData();
  }, []);

  const form = useFormContext<LayoutDataType>();

  const { refreshUser } = useAccount();

  const inputRef = React.useRef<HTMLIonInputElement>(null);

  const formEmail = form.watch('email');

  const isValidEmail = checkIsValidateEmail(formEmail);

  const { createStripeClient, isCreatingStripeClient } =
    useCreateStripeClient();

  React.useEffect(() => {
    if (isPlatform('android') || isPlatform('desktop')) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      });
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      }, 1000);
    }
  }, []);

  const handleNextStep = async (): Promise<void> => {
    if (!formEmail || !isValidEmail) return;

    try {
      const lowerCaseEmail = formEmail.toLowerCase().trim();

      trackOnboardingComplete({ email: lowerCaseEmail });

      await Promise.all([
        createStripeClient(lowerCaseEmail),
        userService.createOrUpdate({
          onboarding_data: {
            onboarded: true,
          },
        }),
      ]);

      refreshUser();
    } catch (error) {
      console.error('Can not create a stripe user', error);
    }
  };

  return (
    <PageStyles background="domination">
      <OnboardingNavigation />

      <IonContent>
        <OnboardingTitle className="onboarding-email-title">
          You are almost there!
        </OnboardingTitle>
        <OnboardingSubtitle>
          Leave your email bellow and start your amazing journey
        </OnboardingSubtitle>

        <Image src={'/assets/layout-own-ai-dominate/email-picture.png'} />

        <OnboardingTitle className="email-title">
          She doesn’t like to be kept waiting. Enter your email
        </OnboardingTitle>

        <div className="email-input-container">
          <FormInput
            form={form}
            hidden={false}
            onSubmit={handleNextStep}
            isShowButton={isValidEmail}
            ref={inputRef}
            name="email"
            type="email"
            placeholder="Type your email"
            loading={isCreatingStripeClient}
            theme={'domination'}
          />
        </div>
        <div className="email-protection">
          <InfoProtection />
        </div>
      </IonContent>
    </PageStyles>
  );
};
